import {
  Phone, Mail, MessageCircle, MessageSquare, Linkedin, Instagram,
  Video, MapPin, Send, MessagesSquare, UserPlus, StickyNote, Facebook,
  Shield, Search, Globe, Building2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  OUTREACH_CHANNELS,
  CHANNEL_GROUPS,
  RESEARCH_SHORTCUTS,
  buildOutreachUrl,
  buildResearchUrl,
  copyOutreachTemplate,
  labelForChannel,
} from '../../config/salesCrm';
import { toast } from 'sonner';

const ICONS = {
  Phone, Mail, MessageCircle, MessageSquare, Linkedin, Instagram,
  Video, MapPin, Send, MessagesSquare, UserPlus, StickyNote, Facebook,
  Shield, Search, Globe, Building2,
};

function ChannelButton({ channel, lead, onSelectChannel, compact }) {
  const Icon = ICONS[channel.icon] || StickyNote;
  const canLaunch = channel.canLaunch && buildOutreachUrl(channel.value, lead);

  const handleClick = async () => {
    if (channel.copyTemplate) {
      const copied = await copyOutreachTemplate(channel.value, lead);
      if (copied) toast.success('Message copied — paste into DM after profile opens');
    }
    const url = buildOutreachUrl(channel.value, lead);
    if (url && channel.canLaunch) {
      window.open(url, channel.value === 'email' ? '_self' : '_blank', 'noopener,noreferrer');
    } else if (!channel.canLaunch) {
      toast.info(`Log your ${channel.label.toLowerCase()} below after you complete it.`);
    } else {
      toast.warning(`Add contact details for ${channel.label} first (phone, email, or profile URL).`);
    }
    onSelectChannel?.(channel.value);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size={compact ? 'sm' : 'default'}
      className={`${compact ? 'h-8 px-2 text-xs' : 'h-auto py-2 flex-col gap-1'} ${canLaunch || !channel.canLaunch ? '' : 'opacity-75'}`}
      onClick={handleClick}
      title={labelForChannel(channel.value)}
    >
      <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      {!compact && <span className="text-[10px] leading-tight text-center">{channel.label}</span>}
    </Button>
  );
}

/**
 * Grouped one-click launch: direct, social, visits, research — then log the touch.
 */
export default function OutreachChannelBar({ lead, onSelectChannel, compact = false }) {
  const handleResearch = (shortcut) => {
    const url = buildResearchUrl(shortcut.value, lead);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.info(shortcut.hint);
    }
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {OUTREACH_CHANNELS.map((ch) => (
          <ChannelButton key={ch.value} channel={ch} lead={lead} onSelectChannel={onSelectChannel} compact />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {CHANNEL_GROUPS.map((group) => {
        const channels = OUTREACH_CHANNELS.filter((c) => c.group === group.id);
        if (!channels.length) return null;
        return (
          <div key={group.id}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">{group.label}</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {channels.map((ch) => (
                <ChannelButton key={ch.value} channel={ch} lead={lead} onSelectChannel={onSelectChannel} />
              ))}
            </div>
          </div>
        );
      })}

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Research before you call</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {RESEARCH_SHORTCUTS.map((shortcut) => {
            const Icon = ICONS[shortcut.icon] || Search;
            return (
              <Button
                key={shortcut.value}
                type="button"
                variant="outline"
                className="h-auto py-2 flex-col gap-1 bg-slate-50"
                onClick={() => handleResearch(shortcut)}
                title={shortcut.hint}
              >
                <Icon className="h-4 w-4 text-slate-600" />
                <span className="text-[10px] leading-tight text-center">{shortcut.label}</span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}